"use client";

import { useEffect, useMemo, useState } from "react";
import { simulatePassOdds } from "@/lib/tools/pass-odds";
import { formatUsd } from "@/lib/utils";
import { Field, NumberInput, Out } from "@/components/tools/form-controls";

type Inputs = {
  accountSize: number;
  winRate: number;
  rr: number;
  riskPct: number;
  target: number;
  maxDD: number;
  days: number;
  seed: number;
};

const DEFAULTS: Inputs = {
  accountSize: 100_000,
  winRate: 45,
  rr: 2,
  riskPct: 1,
  target: 10,
  maxDD: 10,
  days: 30,
  seed: 42,
};

/**
 * useDebounced returns a value that lags `source` by `delayMs`. Used to
 * keep the 10K-sim Monte Carlo off the main thread per keystroke.
 */
function useDebounced<T>(source: T, delayMs: number): T {
  const [v, setV] = useState(source);
  useEffect(() => {
    const t = setTimeout(() => setV(source), delayMs);
    return () => clearTimeout(t);
  }, [source, delayMs]);
  return v;
}

export function OddsClient() {
  const [inputs, setInputs] = useState<Inputs>(DEFAULTS);
  // Debounce so typing in any field doesn't fire 10K sims per keystroke.
  const debounced = useDebounced(inputs, 250);

  const result = useMemo(
    () =>
      simulatePassOdds(
        {
          startingBalance: debounced.accountSize,
          winRatePct: debounced.winRate,
          riskRewardRatio: debounced.rr,
          riskPerTradePct: debounced.riskPct,
          profitTargetPct: debounced.target,
          maxDrawdownPct: debounced.maxDD,
          tradingDays: debounced.days,
          sims: 10_000,
        },
        debounced.seed,
      ),
    [debounced],
  );

  const update = (k: keyof Inputs) => (v: number) => setInputs((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Account size">
          <NumberInput value={inputs.accountSize} onChange={update("accountSize")} step={1000} min={100} />
        </Field>
        <Field label="Win rate (%)">
          <NumberInput value={inputs.winRate} onChange={update("winRate")} step={1} min={1} max={99} />
        </Field>
        <Field label="Risk : reward">
          <NumberInput value={inputs.rr} onChange={update("rr")} step={0.1} min={0.1} max={10} />
        </Field>
        <Field label="Risk per trade (%)">
          <NumberInput value={inputs.riskPct} onChange={update("riskPct")} step={0.1} min={0.1} max={5} />
        </Field>
        <Field label="Profit target (%)">
          <NumberInput value={inputs.target} onChange={update("target")} step={0.5} min={1} max={50} />
        </Field>
        <Field label="Max drawdown (%)">
          <NumberInput value={inputs.maxDD} onChange={update("maxDD")} step={0.5} min={1} max={50} />
        </Field>
        <Field label="Trading days">
          <NumberInput value={inputs.days} onChange={update("days")} step={1} min={1} max={365} />
        </Field>
      </div>

      <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Out label="Pass rate" value={`${result.passRatePct}%`} highlight />
          <Out
            label="Avg trades to pass"
            value={result.averageTradesToPass == null ? "—" : `${result.averageTradesToPass}`}
          />
          <Out label="Median ending balance" value={formatUsd(result.median)} />
          <Out
            label="Avg trades to bust"
            value={result.averageTradesToBust == null ? "—" : `${result.averageTradesToBust}`}
          />
        </div>
        <p className="mt-4 text-sm text-[var(--fg-muted)] leading-relaxed">
          10,000 simulations. Pass rate is the fraction reaching the profit target before busting on max drawdown
          or running out of days. Assumes static (not trailing) drawdown.
        </p>
        <button
          type="button"
          onClick={() => setInputs((p) => ({ ...p, seed: p.seed + 1 }))}
          className="mt-4 text-xs uppercase tracking-wide text-[var(--fg-muted)] hover:text-[var(--brand)] transition-colors"
        >
          Re-run with a new seed
        </button>
      </div>
    </div>
  );
}
